    import { useEffect, useState } from "react";
    import { fetchAssignedTemplateForChruchAsync, fetchAssignedTemplateForDistrictAsync, fetchAssignedTemplateForNationAsync, fetchTemplateAsync } from "@features/report/template/services/reportTemplateSvc";
    import { ReportTemplate, TemplateListQuery, TemplateListResult } from "../types/reportTemplate";
    import { fetchAllPages } from "@core/utils/fetchAllPages";

    /**
     * useReportTemplates
     *
     * Minimal hook doc: fetches and manages a paginated list of report templates
     * using fetchTemplateAsync(departmentId, params). Exposes data, pagination
     * metadata, loading, and a reload() function.
     *
     * @param params TemplateListQuery
     * @returns TemplateListResult
     */
    export function useReportTemplates(params: TemplateListQuery): TemplateListResult {
        const [current_page, setCurrentPage] = useState(params.page || 1);
        const [data, setData] = useState<ReportTemplate[]>([]);
        const [last_page, setLastPage] = useState<number | undefined>(undefined);
        const [per_page, setPerPage] = useState<number | undefined>(undefined);
        const [total, setTotal] = useState(0);
        const [loading, setLoading] = useState(true);

        /**
         * Reloads the report templates list and updates pagination state.
         *
         * This async function:
         * - Sets a loading flag to true while the fetch is in progress.
         * - Extracts `departmentId` from the surrounding `params` and passes the remaining
         *   properties as fetch parameters to `fetchTemplateAsync(departmentId, fetchParams)`.
         * - If a response is returned, reads its data and updates the following pieces of state:
         *   - current page (defaults to 1 if missing)
         *   - list data (defaults to an empty array)
         *   - first/last page URLs, next/previous page URLs
         *   - pagination metadata: from, last_page, links, path, per_page, to, total (total defaults to 0)
         * - Always resets the loading flag to false in a finally block.
         *
         * Notes:
         * - Errors thrown by `fetchTemplateAsync` are not caught here and will propagate to the caller.
         * - Relies on closure variables (`params`, `fetchTemplateAsync`, and the various state setters)
         *   being available in scope.
         *
         * @async
         * @returns {Promise<void>} A promise that resolves when the reload operation completes.
         * @throws {any} Propagates any error thrown by `fetchTemplateAsync`.
         */
        const reload = async () => {
            if (!params.departmentId) {
                setData([]);
                setTotal(0);
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const { departmentId, churchId, districtId, nationId, ...fetchParams } = params;
                const fetchTemplatesForScope = (page: number) => {
                    if (churchId) {
                        return fetchAssignedTemplateForChruchAsync(churchId, departmentId, { ...fetchParams, page });
                    }
                    if (districtId) {
                        return fetchAssignedTemplateForDistrictAsync(districtId, departmentId, { ...fetchParams, page });
                    }
                    if (nationId) {
                        return fetchAssignedTemplateForNationAsync(nationId, departmentId, { ...fetchParams, page });
                    }
                    return fetchTemplateAsync(departmentId, { ...fetchParams, page });
                };

                if (fetchParams.all) {
                    // Fetch all department templates when all=true to avoid the 20-item cap.
                    const allItems = await fetchAllPages<ReportTemplate>((page: number) => fetchTemplatesForScope(page));
                    setData(allItems);
                    setTotal(allItems.length);
                    setCurrentPage(1);
                    setLastPage(1);
                } else {
                    const response = await fetchTemplatesForScope(fetchParams.page ?? 1);
                    if (response) {
                        const d = response.getData();
                        setCurrentPage(d?.current_page || 1);
                        setData(d?.data || []);
                        setLastPage(d?.last_page);
                        setPerPage(d?.per_page);
                        setTotal(d?.total || 0);
                    }
                }
            } finally {
                setLoading(false);
            }
        };

        // Automatically reload when any of the params change
        // eslint-disable-next-line react-hooks/exhaustive-deps
        useEffect(() => { reload(); }, [params.departmentId, params.churchId, params.districtId, params.nationId, params.page, params.perPage, params.all, params.isActive, params.freetext, params.reportTemplateName, params.reportTemplateId, params.description, params.activityName, params.activityId, params.kpiName, params.kpiId, params.originScope]);

        // Return the pagination state and reload function
        return { data, total, current_page, per_page, last_page, loading, reload };
    }